import { PropsWithChildren, useEffect, useState } from "react";
import { MDXProvider } from "@mdx-js/react";
import {
  PageLayout,
  Section,
  FlexColumns,
} from "../../mdx-pages/components/layout";
import {
  AccentText,
  FormTitle,
  SectionTitle,
  AccentTitle,
} from "../../mdx-pages/components/typography";
import { mdxPage, layout } from "../../mdx-pages/components/layout/styles.css";
import { ImageWrapper } from "../../mdx-pages/components/content/image-wrapper";
import { PercentValue } from "../../mdx-pages/components/content/percent-value";
import {
  TextLink,
  UnderlinedTextLink,
} from "../../mdx-pages/components/content/link";
import { TwoColumns } from "../../mdx-pages/components/layout/two-columns/TwoColumns";
import { BulletLIstItem } from "../../mdx-pages/components/content/bullet-list-item";
import { ItemsList } from "../../mdx-pages/components/layout/list";
import { PageProps } from "gatsby";
import { PersonCard } from "../../mdx-pages/components/content/person-card";
import { pageTitleContainer } from "./styles.css";
import { PageTitle } from "./components/page-title";
import { PrivacyPolicyFooter } from "./components/privacy-policy-footer";
import { HeroImageWrapper } from "../../mdx-pages/components/content/hero-image-wrapper";
import { HeroSection } from "../../mdx-pages/components/layout/hero-section";
import { LenisProvider } from "../../providers/lenis";
import {
  getPageType,
  isNewsPage,
  isPrivacyPolicy,
} from "../../shared/utils/getPageType";
import classNames from "classnames";
import { FormContainer } from "../../mdx-pages/components/content/form-container";
import { PageDataProvider } from "../../providers/frontmatter";
import * as FrontmatterComponents from "../../mdx-pages/components/frontmatter";
import { Frontmatter } from "../../mdx-pages/types/fontmatter";
import {
  NewsPost,
  NewsPostContent,
  NewsPostImage,
} from "../../mdx-pages/components/layout/news-post";
import { useCustomScrollRestoration } from "../../shared/utils/useScrollRestoration";
import { Footer } from "./components/footer";
import { smoothScroller } from "../../providers/smooth-scroller";
import { Slide } from "../../mdx-pages/components/content/slide";

// Components can be used in MDX files without any imports
const shortcuts = {
  Section,
  HeroSection,
  SectionTitle,
  PercentValue,
  FlexColumns,
  AccentText,
  AccentTitle,
  UnderlinedTextLink,
  TwoColumns,
  BulletLIstItem,
  ItemsList,
  PersonCard,
  TextLink,
  FormContainer,
  FormTitle,
  ImageWrapper,
  HeroImageWrapper,
  NewsPost,
  NewsPostContent,
  NewsPostImage,
  Slide,
  ...FrontmatterComponents,
};

export const MDXPagesLayout = ({
  children,
  props,
}: PropsWithChildren<{
  props: PageProps<unknown, { frontmatter?: Frontmatter }>;
}>) => {
  const { pathname, state } = props.location as {
    pathname: string;
    state: Record<string, string>;
  };
  const { frontmatter } = props.pageContext;
  const isModalPage = getPageType(pathname) === "modal";
  const isNewsPostPage = isNewsPage(pathname);
  const isPrivacyPolicyPage = isPrivacyPolicy(pathname);
  const [mountedPathname, setMountedPathname] = useState("");

  const handlePopstate = () => {
    window.history.replaceState(
      { wasBack: "true" },
      "",
      window.location.pathname,
    );
  };

  useEffect(() => {
    setMountedPathname("mdx-pages");

    window.addEventListener("popstate", handlePopstate);
    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, []);

  const scrollToTop = () => {
    smoothScroller.scrollToTop();
  };

  const safeFrontmatter: Frontmatter = frontmatter ?? { slug: "" };

  useCustomScrollRestoration(`${pathname}`);

  useEffect(() => {
    if (state?.wasBack) {
      const pageScroll =
        Number(sessionStorage.getItem(`scroll${pathname}`)) || 0;
      window.scrollTo(0, pageScroll);
      sessionStorage.removeItem(`scroll${pathname}`);
      window.sessionStorage.clear();
    }
  }, [pathname]);

  return (
    <PageDataProvider frontmatter={safeFrontmatter}>
      <LenisProvider watch={mountedPathname} options={{ syncTouch: true }}>
        <MDXProvider components={shortcuts}>
          <div className={mdxPage}>
            <PageTitle
              props={props}
              className={classNames({
                [pageTitleContainer.modal]:
                  isModalPage && !isPrivacyPolicyPage && !isNewsPostPage,
              })}
            />
            <PageLayout
              className={classNames({
                [layout.noFooter]: isPrivacyPolicyPage,
                [layout.modal]:
                  isModalPage && !isPrivacyPolicyPage && !isNewsPostPage,
              })}
            >
              {children}
            </PageLayout>
            {isPrivacyPolicyPage && (
              <PrivacyPolicyFooter isGermany={pathname.includes("/de/")} />
            )}
            {(!isModalPage || isNewsPostPage) && (
              <Footer onScrollToTop={scrollToTop} />
            )}
          </div>
        </MDXProvider>
      </LenisProvider>
    </PageDataProvider>
  );
};
